import React, { useEffect, useState } from "react";
import { showError } from "toast/toast";
import {
  fetchBrandsByUserSelect2,
  fetchCategoriesByUserSelect2,
} from "api/postTypeApiCalls";
import { getAuthCookie } from "../utils/authCookies";

type SelectOption = { value: string; label: string };

const API_BASE = process.env.REACT_APP_API_URL || "";

export const normalizeCategoryList = (json: any): SelectOption[] => {
  const list: any[] = Array.isArray(json?.data)
    ? json.data
    : Array.isArray(json)
      ? json
      : [];
  return list
    .map((it: any) => ({
      value: String(it?._id ?? it?.id ?? ""),
      label: String(it?.name ?? it?.label ?? "Unnamed Category"),
    }))
    .filter((opt) => opt.value);
};

export async function fetchCategoryOptionsByIds(
  ids: string[],
  token: string
): Promise<SelectOption[]> {
  const uniqueIds = Array.from(new Set(ids.map((id) => String(id)).filter(Boolean)));
  const results = await Promise.all(
    uniqueIds.map(async (id) => {
      try {
        const res = await fetch(
          `${API_BASE}/categories/${encodeURIComponent(id)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        if (!res.ok || data?.error) return null;
        return {
          value: String(data?.id ?? data?._id ?? id),
          label: String(data?.name ?? "Unnamed Category"),
        } as SelectOption;
      } catch {
        return null;
      }
    })
  );
  return results.filter(Boolean) as SelectOption[];
}

export function mergeCategoryOptions(...groups: SelectOption[][]): SelectOption[] {
  const map = new Map<string, SelectOption>();
  for (const group of groups) {
    for (const opt of group) {
      if (opt.value && !map.has(opt.value)) map.set(opt.value, opt);
    }
  }
  return Array.from(map.values());
}

interface BrandCategorySelectorProps {
  userId: string;
  token: string;
  selectedBrand: string;
  selectedCategories: string[];
  onBrandChange: (brandId: string) => void;
  onCategoriesChange: (categoryIds: string[]) => void;
  brandLabel?: string;
  categoryLabel?: string;
  brandPlaceholder?: string;
  categoryPlaceholder?: string;
  disabled?: boolean;
  renderBrandField?: (props: {
    brands: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    label: string;
    placeholder: string;
    disabled: boolean;
  }) => React.ReactNode;
  renderCategoryField?: (props: {
    categories: SelectOption[];
    value: string[];
    onChange: (values: string[]) => void;
    label: string;
    placeholder: string;
    disabled: boolean;
  }) => React.ReactNode;
}

const BrandCategorySelector: React.FC<BrandCategorySelectorProps> = ({
  userId,
  token,
  selectedBrand,
  selectedCategories,
  onBrandChange,
  onCategoriesChange,
  brandLabel = "Brand",
  categoryLabel = "Categories",
  brandPlaceholder = "Select brand",
  categoryPlaceholder = "Select categories",
  disabled = false,
  renderBrandField,
  renderCategoryField,
}) => {
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
  const [allCategories, setAllCategories] = useState<SelectOption[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<SelectOption[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [brandCategoryIds, setBrandCategoryIds] = useState<string[]>([]);
  const [brandResolvedCategories, setBrandResolvedCategories] = useState<SelectOption[]>([]);
  const membershipId = getAuthCookie("membershipId");

  // 1️⃣ Fetch brands by user (Select2 API)
  useEffect(() => {
    if (!userId || !token) return;

    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        const resp = await fetchBrandsByUserSelect2(membershipId ?? "", token, {
          q: "",
          page: 1,
          limit: 500,
        });
        const options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Brand"),
        }));
        setBrandOptions(options);
      } catch (err) {
        console.error("Failed to fetch brands:", err);
        showError(err instanceof Error ? err.message : "Failed to fetch brands");
      } finally {
        setLoadingBrands(false);
      }
    };

    loadBrands();
  }, [userId, token]);

  // 2️⃣ Fetch all categories by user (Select2 API)
  useEffect(() => {
    if (!userId || !token) return;

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const resp = await fetchCategoriesByUserSelect2(membershipId ?? "", token, {
          q: "",
          page: 1,
          limit: 500,
        });
        const options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Category"),
        }));
        setAllCategories(options);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        showError(err instanceof Error ? err.message : "Failed to fetch categories");
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, [userId, token]);

  // 3️⃣ Fetch brand details when a brand is selected
  useEffect(() => {
    if (!selectedBrand || !token) {
      setBrandCategoryIds([]);
      return;
    }

    const fetchBrandDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brands/${selectedBrand}?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        
        if (!response.ok || data?.error) {
          throw new Error(data?.error || "Failed to fetch brand details");
        }

        // Extract category IDs from brand
        const categoryIds = Array.isArray(data?.categories) 
          ? data.categories.map((id: any) => String(id)) 
          : [];
        
        setBrandCategoryIds(categoryIds);
      } catch (err) {
        console.error("Failed to fetch brand details:", err);
        showError(err instanceof Error ? err.message : "Failed to fetch brand details");
        setBrandCategoryIds([]);
      }
    };

    fetchBrandDetails();
  }, [selectedBrand, token]);

  // 3b️⃣ Resolve category names for brand IDs missing from Select2 list
  useEffect(() => {
    if (!selectedBrand || !token || brandCategoryIds.length === 0) {
      setBrandResolvedCategories([]);
      return;
    }

    const missing = brandCategoryIds.filter(
      (id) => !allCategories.some((c) => c.value === id)
    );
    if (missing.length === 0) {
      setBrandResolvedCategories([]);
      return;
    }

    let cancelled = false;
    (async () => {
      const fetched = await fetchCategoryOptionsByIds(missing, token);
      if (!cancelled) setBrandResolvedCategories(fetched);
    })();

    return () => {
      cancelled = true;
    };
  }, [brandCategoryIds, allCategories, selectedBrand, token]);

  // 4️⃣ Filter categories based on brand's category IDs
  useEffect(() => {
    if (!selectedBrand) {
      // ✅ If no brand selected, clear categories
      setFilteredCategories([]);
    } else if (brandCategoryIds.length === 0) {
      // If brand is selected but has no categories, show all categories
      setFilteredCategories(allCategories);
    } else {
      const merged = mergeCategoryOptions(allCategories, brandResolvedCategories);
      setFilteredCategories(
        merged.filter((cat) => brandCategoryIds.includes(cat.value))
      );
    }
  }, [brandCategoryIds, allCategories, brandResolvedCategories, selectedBrand]);

  // Default render functions if not provided
  const defaultRenderBrandField = (props: any) => (
    <div className="mb-3">
      <label className="form-label">
        {props.label} <span className="text-danger">*</span>
      </label>
      <select
        className="form-control"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        disabled={props.disabled || loadingBrands}
      >
        <option value="">{loadingBrands ? "Loading brands..." : props.placeholder}</option>
        {props.brands.map((brand: SelectOption) => (
          <option key={brand.value} value={brand.value}>
            {brand.label}
          </option>
        ))}
      </select>
    </div>
  );

  const defaultRenderCategoryField = (props: any) => (
    <div className="mb-3">
      <label className="form-label">
        {props.label}
      </label>
      <select
        className="form-control"
        multiple
        value={props.value}
        onChange={(e) => {
          const selected = Array.from(e.target.selectedOptions, option => option.value);
          props.onChange(selected);
        }}
        disabled={props.disabled || loadingCategories || !selectedBrand}
      >
        {props.categories.length === 0 && (
          <option disabled>
            {loadingCategories ? "Loading categories..." : "No categories available"}
          </option>
        )}
        {props.categories.map((category: SelectOption) => (
          <option key={category.value} value={category.value}>
            {category.label}
          </option>
        ))}
      </select>
      {!selectedBrand && (
        <small className="text-muted">Please select a brand first</small>
      )}
    </div>
  );

  return (
    <>
      {renderBrandField ? (
        renderBrandField({
          brands: brandOptions,
          value: selectedBrand,
          onChange: onBrandChange,
          label: brandLabel,
          placeholder: brandPlaceholder,
          disabled: disabled || loadingBrands,
        })
      ) : (
        defaultRenderBrandField({
          brands: brandOptions,
          value: selectedBrand,
          onChange: onBrandChange,
          label: brandLabel,
          placeholder: brandPlaceholder,
          disabled: disabled || loadingBrands,
        })
      )}

      {renderCategoryField ? (
        renderCategoryField({
          categories: filteredCategories,
          value: selectedCategories,
          onChange: onCategoriesChange,
          label: categoryLabel,
          placeholder: categoryPlaceholder,
          disabled: disabled || loadingCategories || !selectedBrand,
        })
      ) : (
        defaultRenderCategoryField({
          categories: filteredCategories,
          value: selectedCategories,
          onChange: onCategoriesChange,
          label: categoryLabel,
          placeholder: categoryPlaceholder,
          disabled: disabled || loadingCategories || !selectedBrand,
        })
      )}
    </>
  );
};

export default BrandCategorySelector;

// Export a hook version for more flexibility (Select2 APIs)
export const useBrandCategorySelector = (userId: string, token: string) => {
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([]);
  const [allCategories, setAllCategories] = useState<SelectOption[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<SelectOption[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [brandCategoryIds, setBrandCategoryIds] = useState<string[]>([]);
  const [brandResolvedCategories, setBrandResolvedCategories] = useState<SelectOption[]>([]);
  const membershipId = getAuthCookie("membershipId");

  // 1️⃣ Fetch brands by user (Select2 API)
  useEffect(() => {
    if (!userId || !token) return;

    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        const resp = await fetchBrandsByUserSelect2(membershipId ?? "", token, {
          q: "",
          page: 1,
          limit: 500,
        });
        const options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Brand"),
        }));
        setBrandOptions(options);
      } catch (err) {
        console.error("Failed to fetch brands:", err);
        showError(err instanceof Error ? err.message : "Failed to fetch brands");
      } finally {
        setLoadingBrands(false);
      }
    };

    loadBrands();
  }, [userId, token]);

  // 2️⃣ Fetch all categories by user (Select2 API)
  useEffect(() => {
    if (!userId || !token) return;

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const resp = await fetchCategoriesByUserSelect2(membershipId ?? "", token, {
          q: "",
          page: 1,
          limit: 500,
        });
        const options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Category"),
        }));
        setAllCategories(options);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        showError(err instanceof Error ? err.message : "Failed to fetch categories");
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, [userId, token]);

  // 3️⃣ Fetch brand details when brand is selected to get category IDs, then filter categories
  useEffect(() => {
    if (!selectedBrand || !token) {
      setBrandCategoryIds([]);
      return;
    }

    const isValidBrandId =
      selectedBrand.trim() !== "" &&
      selectedBrand !== "000000000000000000000000" &&
      /^[a-f0-9]{24}$/i.test(selectedBrand);

    if (!isValidBrandId) {
      setBrandCategoryIds([]);
      return;
    }

    let cancelled = false;

    const fetchBrandDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brands/${selectedBrand}?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok || data?.error) {
          throw new Error(data?.error || "Failed to fetch brand details");
        }
        const ids = Array.isArray(data?.categories)
          ? data.categories.map((id: any) => String(id))
          : [];
        setBrandCategoryIds(ids);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch brand details:", err);
        showError(err instanceof Error ? err.message : "Failed to fetch brand details");
        setBrandCategoryIds([]);
      }
    };

    fetchBrandDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedBrand, token]);

  // 3b️⃣ Resolve category names for brand IDs missing from Select2 list
  useEffect(() => {
    if (!selectedBrand || !token || brandCategoryIds.length === 0) {
      setBrandResolvedCategories([]);
      return;
    }

    const missing = brandCategoryIds.filter(
      (id) => !allCategories.some((c) => c.value === id)
    );
    if (missing.length === 0) {
      setBrandResolvedCategories([]);
      return;
    }

    let cancelled = false;
    (async () => {
      const fetched = await fetchCategoryOptionsByIds(missing, token);
      if (!cancelled) setBrandResolvedCategories(fetched);
    })();

    return () => {
      cancelled = true;
    };
  }, [brandCategoryIds, allCategories, selectedBrand, token]);

  // 4️⃣ Filter categories by brand's category IDs (from Select2 all categories)
  useEffect(() => {
    if (!selectedBrand) {
      setFilteredCategories([]);
    } else if (brandCategoryIds.length === 0) {
      setFilteredCategories(allCategories);
    } else {
      const merged = mergeCategoryOptions(allCategories, brandResolvedCategories);
      setFilteredCategories(
        merged.filter((cat) => brandCategoryIds.includes(cat.value))
      );
    }
  }, [brandCategoryIds, allCategories, brandResolvedCategories, selectedBrand]);

  return {
    selectedBrand,
    setSelectedBrand,
    selectedCategories,
    setSelectedCategories,
    brandOptions,
    filteredCategories,
    loadingBrands,
    loadingCategories,
    brandCategoryIds,
  };
};

